import React from 'react';
import qs from 'query-string';
// eslint-disable-next-line import/no-extraneous-dependencies
import CryptoJS from 'crypto-js';
import { PanelProp } from '@brighterion/types/datapanel';
import { API_URLS, MESSAGE_MAP, SECRET_KEY } from './constants';

/**
 * Returns an object created by key-value entries given by URL search parameter.
 */
export const useSearchParams = () => {
    return qs.parse(document.location.search);
};

export const isValidKeyboardEventKey = <T>(event: React.KeyboardEvent<T>): boolean => {
    /** On key press, prevent default behaviour */
    event.preventDefault();
    /** Check if key is 'Space' or 'Enter'  */
    switch (event.key) {
        case ' ':
            return true;
        case 'Enter':
            return true;
        default:
            return false;
    }
};

export type OnClickEvent<T = Element> = React.MouseEvent<T>;

export const handleOnClickEvent = (fn: <T>(event: OnClickEvent<T>) => void) => <T>(event: OnClickEvent<T>): void => {
    event.preventDefault();
    fn(event);
};

export type KeyEvent<T = Element> = React.KeyboardEvent<T>;

export const handleKeyEvent = (fn: <T>(event: KeyEvent<T>) => void) => <T>(event: KeyEvent<T>): void => {
    event.preventDefault();
    if (isValidKeyboardEventKey(event)) {
        fn(event);
    }
};

export const code = (evt: React.KeyboardEvent) => {
    return evt.key.length === 1 ? evt.key.charCodeAt(0) : evt.key;
};

export const isSpaceOrEnterKey = (evt: React.KeyboardEvent) => {
    const keycode = code(evt);
    if (typeof keycode === 'string') {
        return keycode === 'Enter' || keycode === ' ';
    }
    return keycode === 13 || keycode === 32;
};

export const isClickOrSpaceEnter = (evt: React.MouseEvent | React.KeyboardEvent) => {
    return evt.type === 'click' || (evt.type === 'keydown' && isSpaceOrEnterKey(evt as React.KeyboardEvent));
};

export const capitalizeFirstLetter = (input: string | null): string | null => {
    if (input === undefined || input === null || input.length === 0) {
        return input; // Return null or the input if it's an empty string
    }
    return input.charAt(0).toUpperCase() + input.slice(1);
};

export function truncateString(str: string | null, maxLength: number = 13): string | undefined {
    if (str === null || str === undefined) return undefined;
    if (str.length <= maxLength) {
        return str;
    }
    return `${str.slice(0, maxLength)}...`;
}

export function concatenateValuesFromArray(arr: Array<Record<string, any>>): string[] {
    if (!arr) {
        return [];
    }
    return arr.map((obj) => Object.values(obj).join(' '));
}

export const getKeyValue = (item: Record<string, any>, key: string) => {
    if (item?.modified) {
        return item.modified?.[key];
    }
    return item.current?.[key];
};

export const getValueFromObject = (obj: Record<string, string>, delimiter = ' ') => {
    const keys = Object.keys(obj);
    let str = '';
    for (let i = 0; i <= keys.length; i++) {
        if (obj[keys[i]]) {
            str += obj[keys[i]];
            if (i !== keys.length - 1) {
                str += delimiter;
            }
        }
    }
    str = str.trim();
    if (str.charAt(str.length - 1) === ',') {
        str = str.substring(0, str.length - 1);
    }

    return str;
};

export const getCustomDisplayTextFromArrayWithDelimeter = (arr: any[], delimeter = ','): string => {
    if (!arr) {
        return '';
    }
    return arr
        ?.map((currentValue: Record<string, string>, i: number, arrNew: Record<string, string>[]) => {
            if (arrNew.length - 1 === i) {
                return getValueFromObject(currentValue, delimeter === ';' ? ',' : ' ');
            }
            return `${getValueFromObject(currentValue, delimeter === ';' ? ',' : ' ')}${delimeter}`;
        })
        ?.join(' ');
};

export const getApproveRejectURLS = (listType: string, subType: string, action: string) => {
    const updatedAction = action === 'reject' ? 'approve' : action;
    return `${API_URLS.baseURL}/${listType}/${subType}/${updatedAction}`;
};

export const getDeletePendingUrl = (listType: string, subType: string, individualItemId: number) => {
    return `${API_URLS.baseURL}/${listType}/${subType}/pendingChanges/${individualItemId}`;
};

export const getDetailsForFlowModal = (
    list: any,
    sublist: any,
    type: string,
    revisionId?: number,
    listLockedBy?: string
) => {
    let updatedListName = list;
    let updatedSubList = sublist;
    if (updatedSubList === 'Name') {
        updatedListName = 'Noise Words';
        updatedSubList = '';
    } else if (updatedSubList === 'Address') {
        updatedListName = 'Noise Words';
    }
    // Transform Entities to Persons for display
    if (updatedListName === 'Entities') {
        updatedListName = 'Persons';
    }
    const obj: Record<string, any> = {
        unlockList: {
            actionText: 'Unlock list and discard changes',
            title: 'Unlock list for editing?',
            successMessage: `${updatedSubList} ${updatedListName} is unlocked and ${listLockedBy} 
             changes are discarded (Revision ID ${revisionId})`,
            validationMessage: 'Comment is required to unlock list and discard changes',
            errorTitle: `Changes to ${updatedSubList} ${updatedListName} have failed`,
            errorMessage: 'Something has gone wrong.\nTry unlocking the list again?',
            concurrencyErrorTitle: 'List is already unlocked',
            concurrencyErrorMessage: `${updatedSubList} ${updatedListName} is not locked and is available for editing.`
        },
        sendForApproval: {
            actionText: 'Send',
            title: `Send changes for approval for ${updatedSubList} ${updatedListName}?`,
            successMessage: `Changes (Revision ID ${revisionId}) to ${updatedSubList} ${updatedListName} are sent for approval`,
            validationMessage: 'Comment is required to send for approval',
            errorTitle: `Changes to ${updatedSubList} ${updatedListName} have failed`,
            errorMessage: 'Something has gone wrong.Try sending the list again?',
            concurrencyErrorTitle: 'List is not in review status',
            concurrencyErrorMessage: `${updatedSubList} ${updatedListName} has been discarded.`
        },
        approve: {
            actionText: 'Approve',
            title: `Approve changes for ${updatedSubList} ${updatedListName}?`,
            successMessage: 'Approval confirmed. Changes to the list are processing.',
            validationMessage: 'Comment is required to approve changes',
            errorTitle: `Changes to ${updatedSubList} ${updatedListName} have failed`,
            errorMessage: 'Something has gone wrong.\nTry approving the list again?',
            concurrencyErrorTitle: 'List is approved already',
            concurrencyErrorMessage: `${updatedSubList} ${updatedListName} has been approved by another user.`
        },
        reject: {
            actionText: 'Reject',
            title: `Reject changes for ${updatedSubList} ${updatedListName}?`,
            successMessage: 'Rejection Confirmed',
            validationMessage: 'Comment is required to reject changes',
            errorTitle: `Changes to ${updatedSubList} ${updatedListName} have failed`,
            errorMessage: 'Something has gone wrong.Try rejecting the list again?',
            concurrencyErrorTitle: 'List is rejected already',
            concurrencyErrorMessage: `${updatedSubList} ${updatedListName} has been rejected by another user`
        }
    };
    return obj[type];
};

export const getInvalidateQueries = (list: string, subList: string, queryClient: Record<string, any>) => {
    const capitalizedList = capitalizeFirstLetter(list);
    const capitalizedSubList = capitalizeFirstLetter(subList);
    const invalidateQuery = [`get${capitalizedSubList}${capitalizedList}`];
    const invalidateQueryPending = [`${subList}${capitalizedList}PendingChanges`];
    const invalidateQueryReview = [`${subList}${capitalizedList}ReviewChanges`];
    const invalidateQueryRevisionHistory = [`${subList}${capitalizedList}RevisionHistory`];
    queryClient?.invalidateQueries(invalidateQuery);
    queryClient?.invalidateQueries(invalidateQueryPending);
    queryClient?.invalidateQueries(invalidateQueryReview);
    queryClient?.invalidateQueries(invalidateQueryRevisionHistory);
};

export const getMessage = (entryStatus: EntryStatusType, listType: string, isSuccess: boolean) => {
    const message = isSuccess ? MESSAGE_MAP[entryStatus].success : MESSAGE_MAP[entryStatus].error;
    return message.replace('{listType}', listType);
};

export const encrypt = (key: string, value: any) => {
    try {
        const actualKey = key ?? 'storeKey';
        const data = { [actualKey]: value };
        return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
    } catch (e) {
        return null;
    }
};

export const decrypt = (key: string = 'storeKey', encryptedValue: string | CryptoJS.lib.CipherParams = '') => {
    try {
        const actualKey = key ?? 'storeKey';
        const bytes = CryptoJS.AES.decrypt(encryptedValue, SECRET_KEY);
        const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        return decryptedData[actualKey];
    } catch (e) {
        return null;
    }
};

export const splitArray = (data: PanelProp[]) => {
    return [data.filter((_v, index) => !(index % 2)), data.filter((_v, index) => index % 2)];
};

export const generateUniqueId = () => {
    const randomWordArray = CryptoJS.lib.WordArray.random(4);
    const uniqueSeed = randomWordArray.toString(CryptoJS.enc.Base64).substring(0, 6);
    return uniqueSeed;
};

export const generateUniqueKey = (prefix: string, data: any) => {
    const hash = btoa(JSON.stringify(data)).replace(/[^a-zA-Z0-9]/g, '');
    return `${prefix}-${hash}`;
};
